import React, { useMemo } from 'react';
import { Button, Card, Col, Form } from 'react-bootstrap';
import { BsFillTrashFill } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useFormikContext } from 'formik';

import { ButtonSplitGroup } from '@components/Buttons';
import { renderHeaderWithHover } from '@components/Table/Columns';
import { BootstrapTable } from '@components/Table/Table';
import { API_URL, removeApiFromURL } from '@config/const';
import { checkIsReady } from '@features/queryUtils';
import { capacityApi } from '@reducers/capacity';
import { didServiceTypeApi } from '@reducers/order';
import { planDetailsApi } from '@reducers/plan';
import { profileApi } from '@reducers/profile/profile';

const USE_CASE_EXPLANATION_URL = removeApiFromURL(`${API_URL}use-cases/`);

function renderUseCaseHeader(title, fieldName, fieldDescriptions) {
  return (
    <>
      {renderHeaderWithHover(title, fieldName, fieldDescriptions)}
      <span> - </span>
      <a
        href={USE_CASE_EXPLANATION_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        more details
      </a>
    </>
  );
}

function getDynamicFormikFieldProps({ row, fieldName }) {
  const { values, errors, touched } = useFormikContext();
  const rowIndex = row.id - 1;
  let name = `all.${fieldName}`;
  let value = values.all[fieldName];
  let fieldErrors;
  let fieldTouched;
  if (rowIndex !== -1) {
    const fieldID = `did-${row.values.id}`;
    name = `dids.${fieldID}.${fieldName}`;
    value = values.dids[fieldID][fieldName];
    fieldErrors = errors.dids?.[fieldID]?.[fieldName];
    fieldTouched = touched.dids?.[fieldID]?.[fieldName];
  }
  return { name, value, errors: fieldErrors, touched: fieldTouched };
}

const renderActionCell = ({ row, queries }) => {
  const { useDeleteDidMutation, useDeleteBackorderMutation } = queries;
  const { login } = useSelector((state) => state.auth.user);

  const useDeleteMutation = row.original.is_backorder
    ? useDeleteBackorderMutation
    : useDeleteDidMutation;
  const { id: planID, type, prefixID } = useParams();
  const [trigger, result] = useDeleteMutation();
  const { id } = row.values;
  return id ? (
    <Button
      variant="didxl"
      onClick={() => {
        trigger({ id, prefixID, planID, type, customer: login });
      }}
      disabled={result.isLoading}
    >
      <BsFillTrashFill size={17} />
    </Button>
  ) : null;
};

const renderProfileCell = ({ row }) => {
  const location = useLocation();
  const { configured } = location?.state ? location.state : {};
  const profiles = profileApi.useGetForSelectWithoutBlockedQuery();
  const isLoading = profiles.isLoading || profiles.isFetching;
  const isReady = checkIsReady(profiles);
  const { handleChange, handleBlur } = useFormikContext();
  const field = getDynamicFormikFieldProps({ row, fieldName: 'profileID' });
  return (
    <>
      <Form.Select
        name={field.name}
        value={field.value}
        onChange={handleChange}
        onBlur={handleBlur}
        isValid={field.touched && !field.errors}
        isInvalid={field.touched && field.errors}
        disabled={!isReady || configured}
        size="sm"
      >
        <option value="">{isLoading ? 'Loading...' : 'Select end user'}</option>
        {isReady
          ? profiles.data.map((item) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))
          : null}
      </Form.Select>
      <Form.Control.Feedback type="invalid">
        {field.errors}
      </Form.Control.Feedback>
    </>
  );
};

const renderDidServiceTypeCell = ({ row }) => {
  const location = useLocation();
  const { configured } = location?.state ? location.state : {};
  const serviceTypes = didServiceTypeApi.useGetForSelectQuery();
  const isLoading = serviceTypes.isLoading || serviceTypes.isFetching;
  const isReady = checkIsReady(serviceTypes);

  const { handleChange, handleBlur } = useFormikContext();
  const field = getDynamicFormikFieldProps({
    row,
    fieldName: 'didServiceType',
  });
  return (
    <>
      <Form.Select
        name={field.name}
        value={field.value}
        onChange={handleChange}
        onBlur={handleBlur}
        isValid={field.touched && !field.errors}
        isInvalid={field.touched && field.errors}
        disabled={!isReady || configured}
        size="sm"
      >
        <option value="">{isLoading ? 'Loading...' : 'Select use case'}</option>
        {isReady
          ? serviceTypes.data.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))
          : null}
      </Form.Select>
      <Form.Control.Feedback type="invalid">
        {field.errors}
      </Form.Control.Feedback>
    </>
  );
};

const renderTrunkCell = ({ row }) => {
  const location = useLocation();
  const { configured } = location?.state ? location.state : {};
  const trunks = capacityApi.useGetUserTrunksForSelectQuery();
  const isLoading = trunks.isLoading || trunks.isFetching;
  const isReady = checkIsReady(trunks);

  const { handleChange, handleBlur } = useFormikContext();
  const field = getDynamicFormikFieldProps({
    row,
    fieldName: 'trunkID',
  });
  return (
    <>
      <Form.Select
        name={field.name}
        value={field.value}
        onChange={handleChange}
        onBlur={handleBlur}
        isValid={field.touched && !field.errors}
        isInvalid={field.touched && field.errors}
        disabled={!isReady || configured}
        size="sm"
      >
        <option value="">{isLoading ? 'Loading...' : 'Select trunk'}</option>
        {isReady
          ? trunks.data.map((item) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))
          : null}
      </Form.Select>
      <Form.Control.Feedback type="invalid">
        {field.errors}
      </Form.Control.Feedback>
    </>
  );
};

const renderNumberCell = ({ row, value }) =>
  row.values.id ? (
    value
  ) : (
    <Col className="text-center">
      <span className="fw-bold fst-italic">
        {value} {'>>'}
      </span>
    </Col>
  );

export const ConfigureNumbers = ({ data, queries }) => {
  const location = useLocation();
  const { configured } = location?.state ? location.state : {};
  const query = planDetailsApi.useGetFieldDescriptionQuery();
  const fieldDescriptions = query.data || {};
  const { handleSubmit, setFieldValue } = useFormikContext();
  const navigate = useNavigate();
  const allNumbers = [{ id: null, did: 'Apply to All' }, ...data];
  const columns = useMemo(
    () => [
      {
        Header: () =>
          renderHeaderWithHover('Country', 'country', fieldDescriptions),
        accessor: 'country',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('Area', 'area', fieldDescriptions),
        accessor: 'area',
        className: 'col-md-1',
      },
      {
        Header: () => renderHeaderWithHover('Plan', 'plan', fieldDescriptions),
        accessor: 'plan',
        className: 'col-md-1',
      },
      {
        Header: () =>
          renderHeaderWithHover('# DIDs', 'amount', fieldDescriptions),
        accessor: 'amount',
        className: 'col-md-1',
      },
      {
        id: 'id',
        Header: '',
        accessor: 'id',
      },
      {
        Header: () => renderHeaderWithHover('Number', 'did', fieldDescriptions),
        accessor: 'did',
        className: 'col-md-1',
        Cell: renderNumberCell,
      },
      {
        Header: () =>
          renderHeaderWithHover('End user', 'profile', fieldDescriptions),
        accessor: 'profile',
        className: 'col-md-2',
        Cell: renderProfileCell,
      },
      {
        Header: () =>
          renderHeaderWithHover('Trunk', 'trunk', fieldDescriptions),
        accessor: 'trunk',
        className: 'col-md-2',
        Cell: renderTrunkCell,
      },
      {
        Header: () =>
          renderUseCaseHeader('Use Case', 'use_case', fieldDescriptions),
        accessor: 'didServiceType',
        className: 'col-md-2',
        Cell: renderDidServiceTypeCell,
      },
      {
        Header: () =>
          renderHeaderWithHover('Action', 'action', fieldDescriptions),
        accessor: 'action',
        className: 'col-md-1 text-center',
        Cell: ({ row }) => renderActionCell({ row, queries }),
      },
    ],
    []
  );

  return (
    <Card>
      <Card.Header className="bg-didxl text-white">Configure DIDs</Card.Header>
      <Card.Body>
        <BootstrapTable
          columns={columns}
          data={allNumbers}
          tableProps={() => ({
            responsive: true,
            bordered: true,
            className: 'normalFontSize',
          })}
          hiddenColumns="id"
        />
        <ButtonSplitGroup>
          <Button
            onClick={() => navigate('/order/numbers')}
            variant="didxl"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            variant="didxl"
            size="sm"
            onClick={(e) => {
              setFieldValue('checkout', false);
              handleSubmit(e);
            }}
          >
            Buy and continue shopping
          </Button>
          <Button
            variant="didxl"
            size="sm"
            onClick={(e) => {
              setFieldValue('checkout', true);
              setFieldValue('configured', configured);
              handleSubmit(e);
            }}
          >
            Buy and Check-out
          </Button>
        </ButtonSplitGroup>
      </Card.Body>
    </Card>
  );
};
